import { Component } from "react";
import { observer } from "mobx-react";

import type { Message } from "../models.ts";
import { ThinkingIndicator } from "./ThinkingIndicator.tsx";
import { ImgCancel } from "./Images/ImgCancel.tsx";
import { ChatMessageSearchProgress } from "./ChatMessageSearchProgress.tsx";
import { CollapsibleThink } from "./CollapsibleThink.tsx";
import { StreamingMarkdown } from "./StreamingMarkdown.tsx";

type ChatMessageProgressProps = {
  message: Message;
  executingNode: string | null;
  queuePosition: { position: number; total: number } | null;
  onCancel: () => void;
  enableFormatting?: boolean;
};

@observer
export class ChatMessageProgress extends Component<ChatMessageProgressProps> {
  render() {
    const { message, executingNode, queuePosition, onCancel, enableFormatting } = this.props;

    const hasToolProgress = message.toolProgress && (!!message.toolProgress.searchResults?.length || !!(message.toolProgress.sitesProcessing?.length));
    const operationId = message.toolProgress?.operationId;
    const statusText = message.toolProgress?.status;

    const showStatusLine = !!statusText || !hasToolProgress;
    const streamingText = message.toolProgress?.streamingText;
    const thinkingText = message.toolProgress?.thinkingText;

    return (<>
      {statusText && <ThinkingIndicator text={statusText} />}
      {showStatusLine && !statusText && !streamingText && <ThinkingIndicator text={executingNode ? `Executing: ${executingNode}` : 'Executing tool'} />}
      {queuePosition && <div className="tool-progress-queue">
        <ThinkingIndicator text={`In ComfyUI queue — position ${queuePosition.position} of ${queuePosition.total}`} />
        {operationId && <div className="tool-progress-cancel search-cancel">
          <button onClick={onCancel} title="Cancel generation">
            <ImgCancel size={20} />
            <span>Cancel</span>
          </button>
        </div>}
      </div>}

      {/* Sub-agent thinking block (per-iteration, live) */}
      {thinkingText && (
        <CollapsibleThink text="Sub-agent thinking" isAnimating className="sub-agent-think">
          <pre className="sub-agent-think-content">{thinkingText}</pre>
        </CollapsibleThink>
      )}

      {/* Streaming transcription / sub-agent content text */}
      {streamingText && <StreamingMarkdown content={streamingText} isGenerating enableFormatting={enableFormatting ?? false} />}

      {/* Web search progress display */}
      {hasToolProgress && <ChatMessageSearchProgress message={message} />}

      {/* Cancel button for web search */}
      {hasToolProgress && operationId && <div className="tool-progress-cancel search-cancel">
        <button onClick={onCancel} title="Cancel search">
          <ImgCancel size={20} />
          <span>Cancel</span>
        </button>
      </div>}
    </>);
  }
}
